import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, CloudRain, Play, Upload } from 'lucide-react';
import { sitesService } from '../../services/sitesService';
import { LiteSiteDetailContent } from './components/LiteSiteDetailContent';

// Browse path for the unified site-details surface (EP-LITEPARITY): the same
// LiteSiteDetailContent the inspection flow mounts in its drawer, plus the
// back bar and the Start Inspection CTA.
export function LiteSiteDetailPage() {
  const { siteId } = useParams<{ siteId: string }>();

  // Deduped against LiteSiteDetailContent's ['site', siteId] query — only
  // feeds the breadcrumb name.
  const { data: site } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesService.getSite(siteId!),
    enabled: !!siteId,
    retry: 1,
  });

  if (!siteId) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">Site not found</p>
        <Link
          to="/lite/sites"
          className="text-primary-600 hover:text-primary-700 mt-4 inline-block"
        >
          ← Back to sites
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link
          to="/lite/sites"
          className="flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </Link>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          <span className="text-slate-400 dark:text-slate-500">Sites</span>
          <span className="mx-1.5">/</span>
          <span className="text-slate-700 dark:text-slate-300 font-medium">
            {site?.name ?? '…'}
          </span>
        </div>
      </div>

      {/* CTA first (Jim 8/9): inspectors land here to inspect — no scroll
          hunt past the section stack. */}
      <Link
        to={`/lite/inspect/${siteId}`}
        className="w-full px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 active:bg-primary-800 font-medium flex items-center justify-center gap-2 transition-colors"
      >
        <Play className="w-5 h-5" />
        Start Inspection
      </Link>
      {/* JIM-1030-13 (Meet 10/30): when the weather trigger misses a storm,
          the inspector starts the rain-event visit by hand instead of being
          pushed into a routine + rain combo. Same flow as the Rain Event list. */}
      {site?.rainfall_inspection_required && (
        <Link
          to={`/lite/inspect/${siteId}?rain=1`}
          data-testid="start-rain-event"
          className="w-full px-4 py-2.5 border border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 rounded-xl hover:bg-sky-50 dark:hover:bg-sky-900/20 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
        >
          <CloudRain className="w-4 h-4" />
          Rain event inspection
        </Link>
      )}
      {/* JIM-0904-11: some jurisdictions require their own form — attach it
          here instead of answering questions; stages and corrective actions
          are still recorded. */}
      <Link
        to={`/lite/inspect/${siteId}/upload`}
        className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
      >
        <Upload className="w-4 h-4" />
        Upload a state / city inspection form instead
      </Link>

      <LiteSiteDetailContent siteId={siteId} />
    </div>
  );
}
